import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faDownload } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

const toDateString = (date) => date.toISOString().slice(0, 10);

const getDefaultDates = () => {
    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
        from: toDateString(firstOfMonth),
        to: toDateString(today),
        max: toDateString(today),
    };
};

const ExportModal = ({ isOpen, onClose, onExport, loading = false, title }) => {
    const { t } = useTranslation();
    const effectiveTitle = title ?? t('common.export.title');
    const defaults = getDefaultDates();
    const [fromDate, setFromDate] = useState(defaults.from);
    const [toDate, setToDate] = useState(defaults.to);
    const [format, setFormat] = useState('csv');

    if (!isOpen) return null;

    const today = toDateString(new Date());

    const handleFromDateChange = (val) => {
        setFromDate(val);
        // if from > to, pull to forward to match
        if (val > toDate) setToDate(val);
    };

    const handleToDateChange = (val) => {
        setToDate(val);
        // if to < from, pull from back to match
        if (val < fromDate) setFromDate(val);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (fromDate && toDate) {
            onExport({ from_date: fromDate, to_date: toDate, format });
        }
    };

    const inputStyle = {
        backgroundColor: UI_COLORS.FORMS.INPUT.background,
        borderColor: UI_COLORS.FORMS.INPUT.border,
        color: UI_COLORS.TEXT.BLACK || UI_COLORS.FORMS.INPUT.text,
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{effectiveTitle}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <FontAwesomeIcon icon={faTimes} style={{ color: UI_COLORS.TEXT.SECONDARY }} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>
                            {t('common.export.fromDate')} <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => handleFromDateChange(e.target.value)}
                            max={toDate || today}
                            required
                            className="w-full px-3 py-2 rounded-xl border-2"
                            style={inputStyle}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>
                            {t('common.export.toDate')} <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => handleToDateChange(e.target.value)}
                            min={fromDate}
                            max={today}
                            required
                            className="w-full px-3 py-2 rounded-xl border-2"
                            style={inputStyle}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>
                            {t('common.export.format')}
                        </label>
                        <select
                            value={format}
                            onChange={(e) => setFormat(e.target.value)}
                            className="w-full px-3 py-2 rounded-xl border-2"
                            style={inputStyle}
                        >
                            <option value="csv">{t('common.export.csv')}</option>
                            <option value="excel">{t('common.export.excel')}</option>
                        </select>
                    </div>

                    <div className="flex gap-3 mt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="flex-1 px-4 py-2 rounded-xl border-2 font-semibold transition-all disabled:opacity-50"
                            style={{
                                borderColor: UI_COLORS.BORDERS.MEDIUM,
                                color: UI_COLORS.TEXT.PRIMARY,
                            }}
                        >
                            {t('common.export.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !fromDate || !toDate}
                            className="flex-1 px-4 py-2 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            style={{
                                backgroundColor: UI_COLORS.STATUS.SUCCESS,
                                color: '#fff',
                            }}
                        >
                            <FontAwesomeIcon icon={faDownload} className={loading ? 'animate-spin' : ''} />
                            {loading ? t('common.export.exporting') : t('common.export.exportButton')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ExportModal;
